/*!
 * Bodies of the pinball SigmaBot: an outline and the face anchors of each body.
 *
 * Ported from sigmashake-vcs web/bots/parts-body.ts (the `BODIES` table).
 * Upstream project: ClaudeAnimationBase - https://github.com/JohnHeibel/ClaudeAnimationBase
 * MIT License. Copyright (c) 2026 John Heibel. The full MIT license text is in NOTICE.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */
// SPDX-License-Identifier: GPL-3.0-or-later
// Changes from upstream: the numbers are the same. The 2D draw closures (`back`, `front`) are
// replaced by the name of an extra part (`extra`) that `build.ts` makes in 3D. The arm drawing
// of upstream is not ported.
// Units are body units. The origin is the foot anchor (bottom center). In this table the outline
// keeps the 2D axis: up is negative y. The loft and the builder flip it.

import { ellPts, type Point, rrPts, TAU } from "./kit.ts";
import type { SigmaBotBody } from "./spec.ts";

/** An extra part behind or on the body. */
export type BodyExtra = "bunnyEars" | "catEars" | "robotBolts";

/** The numbers of one body. */
export interface BodyDef {
  readonly pts: Point[];
  readonly rx: number;
  readonly topY: number;
  /** Lowest y of the outline. */
  readonly botY: number;
  /** Center of the body. */
  readonly midY: number;
  readonly faceY: number;
  readonly eyeDx: number;
  readonly eyeR: number;
  readonly mouthY: number;
  readonly shoulderY: number;
  readonly armDx: number;
  readonly armLen: number;
  readonly footDx: number;
  /** Half width where a hat sits. */
  readonly hatW: number;
  readonly neckY: number;
  readonly neckHalf: number;
  readonly extra?: BodyExtra;
}

/** The pear outline: narrow on top, wide at the bottom. */
function pearPts(): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < 26; i++) {
    const a = (i / 26) * TAU;
    out.push({ x: Math.cos(a) * (27 + 8 * Math.sin(a)), y: -38 + Math.sin(a) * 36 });
  }
  return out;
}

/** The numbers after the outline, in the order of the table below. */
type Row = readonly [
  rx: number,
  topY: number,
  botY: number,
  midY: number,
  faceY: number,
  eyeDx: number,
  eyeR: number,
  mouthY: number,
  shoulderY: number,
  armDx: number,
  armLen: number,
  footDx: number,
  hatW: number,
  neckY: number,
  neckHalf: number,
];

function def(pts: Point[], r: Row, extra?: BodyExtra): BodyDef {
  return {
    pts,
    rx: r[0],
    topY: r[1],
    botY: r[2],
    midY: r[3],
    faceY: r[4],
    eyeDx: r[5],
    eyeR: r[6],
    mouthY: r[7],
    shoulderY: r[8],
    armDx: r[9],
    armLen: r[10],
    footDx: r[11],
    hatW: r[12],
    neckY: r[13],
    neckHalf: r[14],
    ...(extra ? { extra } : {}),
  };
}

export const BODIES: Record<SigmaBotBody, BodyDef> = {
  blob: def(
    ellPts(0, -32, 36, 30, 24),
    [36, -62, -2, -32, -38, 12, 6, -24, -26, 34, 16, 15, 26, -12, 28],
  ),
  bean: def(
    ellPts(0, -42, 26, 40, 26),
    [26, -82, -2, -42, -56, 10, 5.5, -42, -36, 25, 16, 11, 22, -22, 20],
  ),
  box: def(
    rrPts(-32, -60, 32, -4, 10),
    [32, -60, -4, -32, -38, 13, 6, -22, -28, 32, 16, 16, 28, -12, 30],
  ),
  tall: def(
    rrPts(-18, -100, 18, -4, 14),
    [18, -100, -4, -52, -76, 8, 5, -60, -50, 18, 18, 8, 16, -38, 17],
  ),
  tiny: def(
    ellPts(0, -20, 22, 18, 20),
    [22, -38, -2, -20, -23, 8, 4.2, -13, -16, 20, 10, 9, 15, -6, 15],
  ),
  round: def(
    ellPts(0, -36, 34, 34, 26),
    [34, -70, -2, -36, -42, 12, 6, -28, -28, 33, 16, 14, 25, -14, 27],
  ),
  pear: def(pearPts(), [28, -74, -2, -38, -52, 10, 5.5, -38, -30, 31, 16, 14, 19, -16, 28]),
  bunny: def(
    ellPts(0, -38, 28, 34, 24),
    [28, -72, -4, -38, -46, 10, 5.5, -32, -30, 27, 16, 12, 17, -16, 22],
    "bunnyEars",
  ),
  cat: def(
    ellPts(0, -32, 36, 28, 24),
    [36, -60, -4, -32, -38, 12, 6, -24, -26, 34, 16, 15, 18, -12, 28],
    "catEars",
  ),
  robot: def(
    rrPts(-30, -62, 30, -4, 8),
    [30, -62, -4, -33, -40, 12, 6, -24, -28, 30, 16, 14, 26, -12, 28],
    "robotBolts",
  ),
};
